import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ICalls } from '@dataTypes/calls.type'
import { useCallRecordingObjectUrl } from '@hooks/useCallRecording'
import { useAppSelector } from '@store/hooks'
import {
  needsRecordingCanonicalize,
  recordingBlobNameFromRef,
} from '@utils/call-recording-playback.util'
import { formatMinuteTime } from '@utils/time.util'

const speedSelection = ['1.00x', '1.25x', '1.50x', '1.75x', '2.00x']

type MessageCenterCallAudioProps = {
  call: ICalls
  onNeedCanonicalize: (call: ICalls) => void
}

const MessageCenterCallAudio = ({
  call,
  onNeedCanonicalize,
}: MessageCenterCallAudioProps): JSX.Element => {
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [isShowVolume, setIsShowVolume] = useState<boolean>(false)
  const [speedIndex, setSpeedIndex] = useState<number>(0)
  const [volume, setVolume] = useState<number>(100)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const hasRequestedRef = useRef<boolean>(false)
  const progressBarRef = useRef<HTMLDivElement | null>(null)

  const liveRecordingUrl = useAppSelector(
    (state) =>
      state.calls.data?.find((c: ICalls) => c.call_id === call.call_id)
        ?.recording_url,
  )
  const recording_url = liveRecordingUrl ?? call.recording_url
  const blobName = recordingBlobNameFromRef(recording_url)
  const playbackSrc = useCallRecordingObjectUrl(recording_url)
  const needsCanonicalize =
    Boolean(recording_url && call.call_sid) &&
    needsRecordingCanonicalize(recording_url)

  const handlePlayButton = (): void => {
    const _isPlaying = !isPlaying
    if (audioRef.current) {
      setIsPlaying(_isPlaying)
      if (_isPlaying) {
        void audioRef.current.play()
      } else {
        audioRef.current.pause()
      }
    }
  }
  const handleRewind = (): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        audioRef.current.currentTime - 10,
      )
    }
  }

  const handleForward = (): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration,
        audioRef.current.currentTime + 10,
      )
    }
  }

  const handleProgressClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (audioRef.current && progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickPositionRatio = clickX / rect.width
      const newTime = clickPositionRatio * audioRef.current.duration
      audioRef.current.currentTime = newTime
    }
  }

  const handleVolumeButton = (): void => {
    const _isMuted = !isMuted
    if (audioRef.current) {
      setIsMuted(_isMuted)
      audioRef.current.muted = _isMuted
    }
  }

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newVolume = Number(e.target.value)
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100
      if (newVolume === 0) {
        setIsMuted(true)
        audioRef.current.muted = true
      } else if (isMuted) {
        setIsMuted(false)
        audioRef.current.muted = false
      }
    }
  }

  const handleSpeedChange = (): void => {
    const previousSpeedIndex = speedIndex
    if (previousSpeedIndex === speedSelection.length - 1) {
      setSpeedIndex(0)
    } else {
      setSpeedIndex(previousSpeedIndex + 1)
    }
  }

  useEffect(() => {
    if (blobName || !needsCanonicalize) {
      return
    }

    if (
      call.call_id &&
      recording_url &&
      call.call_sid &&
      !hasRequestedRef.current
    ) {
      hasRequestedRef.current = true
      onNeedCanonicalize(call)
    }
  }, [
    blobName,
    call,
    call.call_id,
    call.call_sid,
    needsCanonicalize,
    onNeedCanonicalize,
    recording_url,
  ])

  const updateProgress = useCallback((): void => {
    const audio = audioRef.current
    if (audio && audio.duration > 0) {
      setCurrentTime(audio.currentTime)
      setProgress((audio.currentTime / audio.duration) * 100)
      if (audio.currentTime === audio.duration) {
        setIsPlaying(false)
      }
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateDuration = (): void => {
      if (audio.duration) {
        setDuration(audio.duration)
      }
    }

    const handleLoadedMetadata = (): void => {
      updateDuration()
    }

    const handleTimeUpdate = (): void => {
      updateProgress()
    }

    const handleEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
      setProgress(0)
    }

    const speedValue = parseFloat(speedSelection[speedIndex].replace('x', ''))
    audio.playbackRate = speedValue

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [playbackSrc, speedIndex, updateProgress])

  useEffect(() => {
    const audio = audioRef.current
    if (audio) {
      audio.volume = volume / 100
    }
  }, [volume])

  return (
    <div className="audio-container">
      {playbackSrc ? (
        <>
          <audio
            ref={audioRef}
            controls={false}
            className="audio-player"
            src={playbackSrc}
          />
          <button
            type="button"
            className="unstyled-button time-control-btn rewind-btn mgc_rewind_backward_10_line"
            onClick={handleRewind}
            aria-label="Rewind 10 seconds"
          />
          <button
            type="button"
            className={`unstyled-button play-btn ${isPlaying ? 'mgc_pause_fill' : 'mgc_play_fill'}`}
            onClick={handlePlayButton}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          />
          <button
            type="button"
            className="unstyled-button time-control-btn forward-btn mgc_rewind_forward_10_line"
            onClick={handleForward}
            aria-label="Forward 10 seconds"
          />
          <div className="progress-time-container">
            <span className="time-display">
              {formatMinuteTime(currentTime)}
            </span>
            <div
              className="progress-bar-container"
              ref={progressBarRef}
              onClick={handleProgressClick}
            >
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill"
                  style={{
                    width:
                      progress > 0
                        ? `calc(${progress}% - ${(progress / 100) * 4}px)`
                        : '0px',
                  }}
                >
                  <div
                    className="progress-bar-handle"
                    style={{
                      left: progress === 0 ? '-4px' : `calc(100% - 7px)`,
                    }}
                  />
                </div>
              </div>
            </div>
            <span className="time-display">{formatMinuteTime(duration)}</span>
          </div>
          <div
            className="volume-container"
            onMouseEnter={() => setIsShowVolume(true)}
            onMouseLeave={() => setIsShowVolume(false)}
          >
            <button
              type="button"
              className={`unstyled-button volume-btn mgc_volume${isMuted ? '_mute' : ''}_fill`}
              onClick={handleVolumeButton}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            />
            {isShowVolume && (
              <input
                className="volume-slider"
                type="range"
                min="0"
                max="100"
                step="1"
                value={volume}
                onChange={handleVolumeChange}
                onMouseEnter={() => setIsShowVolume(true)}
                onMouseLeave={() => setIsShowVolume(false)}
              />
            )}
          </div>
          <button
            type="button"
            className="unstyled-button speed-btn"
            onClick={handleSpeedChange}
            aria-label="Change playback speed"
          >
            {speedSelection[speedIndex]}
          </button>
        </>
      ) : blobName || needsCanonicalize ? (
        <p className="mb-0 no-data-container">Loading...</p>
      ) : (
        <p className="mb-0 no-data-container">-</p>
      )}
    </div>
  )
}

export default MessageCenterCallAudio
